import React from 'react';
import {
    Box, Grid, Typography, FormControl, InputLabel, MenuItem, Select,
    Paper, Tabs, Tab, Skeleton, Alert
} from '@mui/material';
import {
    BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
    Tooltip, ResponsiveContainer, Cell
} from 'recharts';
import { useTheme, useMediaQuery } from '@mui/material';
import Colors from "../../components/ui/Colors";
import {
    getDeliveriesByRegion, getDeliveriesByTransporter,
    getDeliveriesByClient, getDeliveriesByMonth,
    getKpiSummary, getInventoryByProduct, getDefectsByProduct,
    getDashboardSummary, getInventoryExpiry, getDefectsByMonthStats
} from "../../utilityFunctions/WarehouseUtil";

// ─── Constants ────────────────────────────────────────────────────────────────

const MONTH_NAMES = ['', 'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const CHART_COLORS = ['#1c2444', '#2563eb', '#0284c7', '#0891b2', '#1e40af', '#3b82f6', '#0d9488', '#1d4ed8'];

// ─── Shared helpers ───────────────────────────────────────────────────────────

function numFmt(n) {
    if (!n && n !== 0) return '—';
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return new Intl.NumberFormat('es-CO').format(Math.round(n));
}

function numFull(n) {
    return new Intl.NumberFormat('es-CO').format(Math.round(n || 0));
}

function shortLabel(str, max = 14) {
    if (!str) return '—';
    return str.length > max ? str.substring(0, max) + '…' : str;
}

// ─── Reusable components ──────────────────────────────────────────────────────

const KpiCard = ({ label, value, sub, color, loading }) => (
    <Paper elevation={0} sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: 2,
        border: '1px solid #e5e7eb',
        borderLeft: `4px solid ${color || Colors.primary}`,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
    }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {label}
        </Typography>
        {loading ? (
            <Skeleton variant="text" width="60%" height={36} />
        ) : (
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: color || Colors.primary, lineHeight: 1.2 }}>
                {value}
            </Typography>
        )}
        {sub && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{sub}</Typography>
        )}
    </Paper>
);

const ChartCard = ({ title, children, loading, minHeight = 240 }) => (
    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #d1d9e6', backgroundColor: '#fff', height: '100%' }}>
        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary', mb: 1, textAlign: 'center' }}>
            {title}
        </Typography>
        {loading ? (
            <Skeleton variant="rectangular" width="100%" height={minHeight} sx={{ borderRadius: 1 }} />
        ) : (
            <Box sx={{ width: '100%', minHeight }}>
                {children}
            </Box>
        )}
    </Paper>
);

const CustomTooltipKg = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    return (
        <Paper elevation={3} sx={{ p: 1.5, borderRadius: 1.5 }}>
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold', mb: 0.5 }}>{label}</Typography>
            {payload.map((p, i) => (
                <Typography key={i} variant="caption" sx={{ display: 'block', color: p.color }}>
                    {p.name}: {numFull(p.value)} kg
                </Typography>
            ))}
        </Paper>
    );
};

const CustomTooltipUnits = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    return (
        <Paper elevation={3} sx={{ p: 1.5, borderRadius: 1.5 }}>
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold', mb: 0.5 }}>{label}</Typography>
            {payload.map((p, i) => (
                <Typography key={i} variant="caption" sx={{ display: 'block', color: p.color }}>
                    {p.name}: {numFull(p.value)} uds
                </Typography>
            ))}
        </Paper>
    );
};

// ─── Period selector helper ───────────────────────────────────────────────────

const PeriodSelector = ({ year, month, setYear, setMonth, showMonth = true }) => {
    const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);
    return (
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 90 }}>
                <InputLabel>Año</InputLabel>
                <Select value={year} label="Año" onChange={(e) => setYear(Number(e.target.value))}>
                    {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                </Select>
            </FormControl>
            {showMonth && (
                <FormControl size="small" sx={{ minWidth: 110 }}>
                    <InputLabel>Mes</InputLabel>
                    <Select value={month} label="Mes" onChange={(e) => setMonth(Number(e.target.value))}>
                        {MONTH_NAMES.slice(1).map((name, i) => (
                            <MenuItem key={i + 1} value={i + 1}>{name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}
        </Box>
    );
};

// ─── Main component ───────────────────────────────────────────────────────────

export default function DeliveryCharts({ loginUser }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Tab state
    const [activeTab, setActiveTab] = React.useState(0);

    // Period state (shared between Despachos + Calidad tabs)
    const [year, setYear] = React.useState(new Date().getFullYear());
    const [month, setMonth] = React.useState(new Date().getMonth() + 1);

    // Tab 1 — Resumen
    const [summary, setSummary] = React.useState(null);
    const [summaryLoading, setSummaryLoading] = React.useState(true);

    // Tab 2 — Despachos
    const [regionData, setRegionData] = React.useState([]);
    const [transporterData, setTransporterData] = React.useState([]);
    const [clientData, setClientData] = React.useState([]);
    const [monthlyData, setMonthlyData] = React.useState([]);
    const [dispatchLoading, setDispatchLoading] = React.useState(true);

    // Tab 3 — Inventario
    const [inventoryData, setInventoryData] = React.useState([]);
    const [inventoryExpiry, setInventoryExpiry] = React.useState(null);
    const [inventoryLoading, setInventoryLoading] = React.useState(true);

    // Tab 4 — Calidad
    const [defectsData, setDefectsData] = React.useState([]);
    const [monthlyDefects, setMonthlyDefects] = React.useState([]);
    const [qualityLoading, setQualityLoading] = React.useState(true);

    const axisStyle = { fontSize: isMobile ? 10 : 11, fill: '#6b7280' };

    // ── Initial data load ──────────────────────────────────────────────────────
    React.useEffect(() => {
        fetchSummaryData();
        fetchInventoryData();
        fetchDefectsByProduct();
    }, []);

    // ── Period-triggered fetch (Despachos) ─────────────────────────────────────
    React.useEffect(() => {
        fetchDispatchData(year, month);
    }, [year, month]);

    // ── Year-triggered fetch (Calidad monthly trend) ───────────────────────────
    React.useEffect(() => {
        fetchMonthlyDefects(year);
    }, [year]);

    // ── Fetch functions ────────────────────────────────────────────────────────

    const fetchSummaryData = async () => {
        setSummaryLoading(true);
        const res = await getDashboardSummary(loginUser.jwtToken);
        if (res) setSummary(res);
        setSummaryLoading(false);
    };

    const fetchInventoryData = async () => {
        setInventoryLoading(true);
        const [invRes, expiryRes] = await Promise.all([
            getInventoryByProduct(loginUser.jwtToken),
            getInventoryExpiry(loginUser.jwtToken),
        ]);
        if (invRes) setInventoryData(invRes.slice(0, 15));
        if (expiryRes) setInventoryExpiry(expiryRes);
        setInventoryLoading(false);
    };

    const fetchDefectsByProduct = async () => {
        setQualityLoading(true);
        const res = await getDefectsByProduct(loginUser.jwtToken);
        if (res) setDefectsData(res.filter(d => d.totalDefectAmount > 0).slice(0, 15));
        setQualityLoading(false);
    };

    const fetchDispatchData = async (y, m) => {
        setDispatchLoading(true);
        const req = { year: y, month: m };
        const [regionRes, transporterRes, clientRes, monthlyRes] = await Promise.all([
            getDeliveriesByRegion(req, loginUser.jwtToken),
            getDeliveriesByTransporter(req, loginUser.jwtToken),
            getDeliveriesByClient(req, loginUser.jwtToken),
            getDeliveriesByMonth({ year: y }, loginUser.jwtToken),
        ]);
        if (regionRes) setRegionData(regionRes.slice(0, 8));
        if (transporterRes) setTransporterData(transporterRes.slice(0, 8));
        if (clientRes) setClientData(clientRes.slice(0, 8));
        if (monthlyRes) {
            const filled = MONTH_NAMES.slice(1).map((name, i) => {
                const found = monthlyRes.find(d => d.monthNumber === i + 1);
                return { name, totalWeight: found ? found.totalWeight : 0 };
            });
            setMonthlyData(filled);
        }
        setDispatchLoading(false);
    };

    const fetchMonthlyDefects = async (y) => {
        const res = await getDefectsByMonthStats({ year: y }, loginUser.jwtToken);
        if (res) {
            const filled = MONTH_NAMES.slice(1).map((name, i) => {
                const found = res.find(d => d.monthNumber === i + 1);
                return { name, totalDefectAmount: found ? found.totalDefectAmount : 0 };
            });
            setMonthlyDefects(filled);
        }
    };

    // ── Tab 1: Resumen ─────────────────────────────────────────────────────────

    const ResumenTab = () => {
        const miniDefectData = monthlyDefects.length > 0 ? monthlyDefects : [];

        return (
            <Box>
                {/* KPI 2×3 grid */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6} sm={4}>
                        <KpiCard
                            label="Viajes activos"
                            value={summaryLoading ? null : numFull(summary?.activeTransportOrders)}
                            sub="En curso ahora"
                            color="#1c2444"
                            loading={summaryLoading}
                        />
                    </Grid>
                    <Grid item xs={6} sm={4}>
                        <KpiCard
                            label="Pedidos pendientes"
                            value={summaryLoading ? null : numFull(summary?.pendingDeliveryOrders)}
                            sub="Sin despachar"
                            color="#0891b2"
                            loading={summaryLoading}
                        />
                    </Grid>
                    <Grid item xs={6} sm={4}>
                        <KpiCard
                            label="Estibas en bodega"
                            value={summaryLoading ? null : numFull(summary?.batchesInWarehouse)}
                            sub="Activas"
                            color="#1e40af"
                            loading={summaryLoading}
                        />
                    </Grid>
                    <Grid item xs={6} sm={4}>
                        <KpiCard
                            label="Unidades en bodega"
                            value={summaryLoading ? null : numFmt(summary?.unitsInWarehouse)}
                            sub="Stock actual"
                            color="#2563eb"
                            loading={summaryLoading}
                        />
                    </Grid>
                    <Grid item xs={6} sm={4}>
                        <KpiCard
                            label="Averías totales"
                            value={summaryLoading ? null : numFmt(summary?.totalDefectUnits)}
                            sub="Acumuladas"
                            color="#ef4444"
                            loading={summaryLoading}
                        />
                    </Grid>
                    <Grid item xs={6} sm={4}>
                        <KpiCard
                            label="Ciclo promedio"
                            value={summaryLoading ? null : (summary?.avgCycleDays != null ? `${summary.avgCycleDays.toFixed(1)} días` : '—')}
                            sub="Entrada a salida"
                            color="#0284c7"
                            loading={summaryLoading}
                        />
                    </Grid>
                </Grid>

                {/* Alert boxes */}
                {!summaryLoading && inventoryExpiry && (inventoryExpiry.expiredCount > 0 || inventoryExpiry.expiring30Count > 0) && (
                    <Alert severity="warning" sx={{ mb: 1.5, borderRadius: 2 }}>
                        {inventoryExpiry.expiredCount > 0 && (
                            <Typography variant="body2">
                                <strong>{inventoryExpiry.expiredCount}</strong> estiba(s) con producto <strong>vencido</strong>
                            </Typography>
                        )}
                        {inventoryExpiry.expiring30Count > 0 && (
                            <Typography variant="body2">
                                <strong>{inventoryExpiry.expiring30Count}</strong> estiba(s) vence(n) en menos de 30 días
                            </Typography>
                        )}
                    </Alert>
                )}
                {!summaryLoading && summary?.pendingDeliveryOrders > 0 && (
                    <Alert severity="info" sx={{ mb: 1.5, borderRadius: 2 }}>
                        <Typography variant="body2">
                            Hay <strong>{numFull(summary.pendingDeliveryOrders)}</strong> pedido(s) pendiente(s) de despacho
                        </Typography>
                    </Alert>
                )}

                {/* Mini monthly defect trend */}
                <ChartCard title={`Tendencia de averías ${year}`} loading={qualityLoading} minHeight={180}>
                    <ResponsiveContainer width="100%" height={180}>
                        <LineChart data={miniDefectData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="name" tick={axisStyle} />
                            <YAxis tick={axisStyle} tickFormatter={numFmt} width={45} />
                            <Tooltip content={<CustomTooltipUnits />} />
                            <Line
                                type="monotone"
                                dataKey="totalDefectAmount"
                                name="Averías"
                                stroke="#ef4444"
                                strokeWidth={2}
                                dot={{ r: 3 }}
                                activeDot={{ r: 5 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </ChartCard>
            </Box>
        );
    };

    // ── Tab 2: Despachos ───────────────────────────────────────────────────────

    const DespachosTab = () => (
        <Box>
            <PeriodSelector year={year} month={month} setYear={setYear} setMonth={setMonth} showMonth={true} />
            <Grid container spacing={2}>
                {/* Monthly trend — full width */}
                <Grid item xs={12}>
                    <ChartCard title="Evolución mensual (kg) — año completo" loading={dispatchLoading} minHeight={200}>
                        <ResponsiveContainer width="100%" height={200}>
                            <LineChart data={monthlyData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="name" tick={axisStyle} />
                                <YAxis tick={axisStyle} tickFormatter={numFmt} width={55} />
                                <Tooltip content={<CustomTooltipKg />} />
                                <Line
                                    type="monotone"
                                    dataKey="totalWeight"
                                    name="kg despachados"
                                    stroke="#2563eb"
                                    strokeWidth={2.5}
                                    dot={{ r: 3, fill: '#2563eb' }}
                                    activeDot={{ r: 5 }}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </Grid>

                {/* Region */}
                <Grid item xs={12} sm={6}>
                    <ChartCard title="Por región" loading={dispatchLoading} minHeight={220}>
                        <ResponsiveContainer width="100%" height={220}>
                            <BarChart data={regionData} margin={{ top: 5, right: 10, left: 10, bottom: 40 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis
                                    dataKey="region"
                                    tick={axisStyle}
                                    tickFormatter={(v) => shortLabel(v, isMobile ? 8 : 12)}
                                    angle={-35}
                                    textAnchor="end"
                                    interval={0}
                                />
                                <YAxis tick={axisStyle} tickFormatter={numFmt} width={50} />
                                <Tooltip content={<CustomTooltipKg />} />
                                <Bar dataKey="totalWeight" name="kg" radius={[3, 3, 0, 0]}>
                                    {regionData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </Grid>

                {/* Transporter */}
                <Grid item xs={12} sm={6}>
                    <ChartCard title="Por transportadora" loading={dispatchLoading} minHeight={220}>
                        <ResponsiveContainer width="100%" height={220}>
                            <BarChart data={transporterData} margin={{ top: 5, right: 10, left: 10, bottom: 40 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis
                                    dataKey="transporter"
                                    tick={axisStyle}
                                    tickFormatter={(v) => shortLabel(v, isMobile ? 8 : 12)}
                                    angle={-35}
                                    textAnchor="end"
                                    interval={0}
                                />
                                <YAxis tick={axisStyle} tickFormatter={numFmt} width={50} />
                                <Tooltip content={<CustomTooltipKg />} />
                                <Bar dataKey="totalWeight" name="kg" radius={[3, 3, 0, 0]}>
                                    {transporterData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </Grid>

                {/* Client top 8 — full width */}
                <Grid item xs={12}>
                    <ChartCard title="Por cliente (top 8)" loading={dispatchLoading} minHeight={220}>
                        <ResponsiveContainer width="100%" height={220}>
                            <BarChart data={clientData} margin={{ top: 5, right: 20, left: 10, bottom: 50 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis
                                    dataKey="clientName"
                                    tick={axisStyle}
                                    tickFormatter={(v) => shortLabel(v, isMobile ? 10 : 18)}
                                    angle={-35}
                                    textAnchor="end"
                                    interval={0}
                                />
                                <YAxis tick={axisStyle} tickFormatter={numFmt} width={55} />
                                <Tooltip content={<CustomTooltipKg />} />
                                <Bar dataKey="totalWeight" name="kg" radius={[3, 3, 0, 0]}>
                                    {clientData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </Grid>
            </Grid>
        </Box>
    );

    // ── Tab 3: Inventario ──────────────────────────────────────────────────────

    const ExpiryBucket = ({ count, label, color, bg }) => (
        <Paper elevation={0} sx={{
            p: 1.5,
            borderRadius: 2,
            border: `1px solid ${color}`,
            backgroundColor: bg,
            textAlign: 'center',
            flex: 1,
            minWidth: 0,
        }}>
            {inventoryLoading ? (
                <Skeleton variant="text" width="50%" height={32} sx={{ mx: 'auto' }} />
            ) : (
                <Typography variant="h5" sx={{ fontWeight: 'bold', color }}>
                    {numFull(count)}
                </Typography>
            )}
            <Typography variant="caption" sx={{ color, fontWeight: 500 }}>{label}</Typography>
        </Paper>
    );

    const InventarioTab = () => (
        <Box>
            {/* Expiry buckets row */}
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                <ExpiryBucket count={inventoryExpiry?.expiredCount} label="Vencidas" color="#dc2626" bg="#fef2f2" />
                <ExpiryBucket count={inventoryExpiry?.expiring30Count} label="< 30 días" color="#d97706" bg="#fffbeb" />
                <ExpiryBucket count={inventoryExpiry?.expiring60Count} label="31-60 días" color="#ca8a04" bg="#fefce8" />
                <ExpiryBucket count={inventoryExpiry?.safeCount} label="Seguras" color="#16a34a" bg="#f0fdf4" />
            </Box>

            {/* Inventory by product — horizontal bar */}
            <ChartCard title="Inventario por producto (unidades en bodega)" loading={inventoryLoading} minHeight={320}>
                {inventoryData.length === 0 ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 320 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>Sin datos de inventario</Typography>
                    </Box>
                ) : (
                    <ResponsiveContainer width="100%" height={320}>
                        <BarChart data={inventoryData} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                            <XAxis type="number" tick={axisStyle} tickFormatter={numFmt} />
                            <YAxis
                                type="category"
                                dataKey="productDescription"
                                tick={axisStyle}
                                tickFormatter={(v) => shortLabel(v, isMobile ? 12 : 22)}
                                width={isMobile ? 90 : 150}
                            />
                            <Tooltip content={<CustomTooltipUnits />} />
                            <Bar dataKey="totalUnits" name="Unidades en bodega" radius={[0, 3, 3, 0]}>
                                {inventoryData.map((_, i) => (
                                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} fillOpacity={0.85} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </ChartCard>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1, textAlign: 'center' }}>
                Actualizado en tiempo real
            </Typography>
        </Box>
    );

    // ── Tab 4: Calidad ─────────────────────────────────────────────────────────

    const CalidadTab = () => (
        <Box>
            <PeriodSelector year={year} month={month} setYear={setYear} setMonth={setMonth} showMonth={false} />
            <Grid container spacing={2}>
                {/* Defects by product — horizontal bar */}
                <Grid item xs={12} sm={6}>
                    <ChartCard title="Averías por producto (unidades)" loading={qualityLoading} minHeight={300}>
                        {defectsData.length === 0 ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>Sin averías registradas</Typography>
                            </Box>
                        ) : (
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={defectsData} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                                    <XAxis type="number" tick={axisStyle} tickFormatter={numFmt} />
                                    <YAxis
                                        type="category"
                                        dataKey="productDescription"
                                        tick={axisStyle}
                                        tickFormatter={(v) => shortLabel(v, isMobile ? 12 : 20)}
                                        width={isMobile ? 90 : 140}
                                    />
                                    <Tooltip content={<CustomTooltipUnits />} />
                                    <Bar dataKey="totalDefectAmount" name="Unidades perdidas" fill="#ef4444" radius={[0, 3, 3, 0]} fillOpacity={0.85} />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </ChartCard>
                </Grid>

                {/* Monthly defect trend */}
                <Grid item xs={12} sm={6}>
                    <ChartCard title={`Tendencia de averías por mes — ${year}`} loading={qualityLoading} minHeight={220}>
                        <ResponsiveContainer width="100%" height={220}>
                            <LineChart data={monthlyDefects} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="name" tick={axisStyle} />
                                <YAxis tick={axisStyle} tickFormatter={numFmt} width={45} />
                                <Tooltip content={<CustomTooltipUnits />} />
                                <Line
                                    type="monotone"
                                    dataKey="totalDefectAmount"
                                    name="Averías"
                                    stroke="#ef4444"
                                    strokeWidth={2}
                                    dot={{ r: 3 }}
                                    activeDot={{ r: 5 }}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </Grid>
            </Grid>
        </Box>
    );

    // ── Render ─────────────────────────────────────────────────────────────────

    return (
        <Box sx={{ width: '100%', height: '100%', overflowY: 'auto', backgroundColor: '#f0f4f8' }}>
            <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 1.5, sm: 3 }, py: { xs: 2, sm: 3 } }}>

                {/* Page header */}
                <Box sx={{
                    mb: 2, px: 2.5, py: 2,
                    borderRadius: 2,
                    backgroundColor: Colors.primary,
                }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#fff' }}>
                        Métricas de Bodega
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)' }}>
                        Resumen general · Despachos · Inventario · Calidad
                    </Typography>
                </Box>

                {/* Tabs */}
                <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid #d1d9e6', mb: 2, backgroundColor: '#fff' }}>
                    <Tabs
                        value={activeTab}
                        onChange={(_, v) => setActiveTab(v)}
                        variant={isMobile ? 'scrollable' : 'fullWidth'}
                        scrollButtons={isMobile ? 'auto' : false}
                        sx={{
                            '& .MuiTab-root': { fontWeight: 600, fontSize: isMobile ? 12 : 13, textTransform: 'none', color: '#64748b' },
                            '& .MuiTabs-indicator': { backgroundColor: Colors.primary, height: 3 },
                            '& .Mui-selected': { color: `${Colors.primary} !important` },
                            borderBottom: '1px solid #e2e8f0',
                        }}
                    >
                        <Tab label="Resumen" />
                        <Tab label="Despachos" />
                        <Tab label="Inventario" />
                        <Tab label="Calidad" />
                    </Tabs>
                </Paper>

                {/* Tab content */}
                <Box>
                    {activeTab === 0 && <ResumenTab />}
                    {activeTab === 1 && <DespachosTab />}
                    {activeTab === 2 && <InventarioTab />}
                    {activeTab === 3 && <CalidadTab />}
                </Box>

            </Box>
        </Box>
    );
}
